import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { ChevronRight } from 'lucide-react'
import playStoreLogo from '../../assets/ps.webp'
import appleStoreLogo from '../../assets/as.webp'
import { details, services } from '../../web-data'
import CustomCard from '../../components/ui/CustomCard'
import smartPhone from '../../assets/App_Mockup_transparent.png'
import dsaLoanExchange from '../../assets/image5.png'
import DashboardVideo from '../../assets/DashboardVideo.mp4'
import dsaLeanManagement from '../../assets/image8.png'
import digitalEarningTools from '../../assets/image6.png'
import { links } from '../../components/header/Links'
import { getServerUrl } from '../../utils/serverHours'

const dsaRegistrationUrl = links.find(({ name }) => name === 'DSA Registration')?.url
const bankerRegistrationUrl = links.find(({ name }) => name === 'Banker Registration')?.url

const trustProofs = [
    {
        label: "Instant paperless workflow",
        text: "Digital steps reduce paperwork, repeated follow-ups, and avoidable delays."
    },
    {
        label: "Trusted by growing DSA teams",
        text: "Built for real daily execution across customers, leads, and follow-ups."
    },
    {
        label: "Bank-level security",
        text: "Sensitive financial information stays guarded through the journey."
    },
    {
        label: "Transparent pricing",
        text: "Customers understand the cost clearly before moving ahead."
    }
]


const platformStats = [
    {
        value: "231",
        label: "Active Users",
        note: "Using digital workflows"
    },
    {
        value: "385",
        label: "Issues Solved",
        note: "Across support journeys"
    },
    {
        value: "159",
        label: "Good Reviews",
        note: "From platform users"
    },
    {
        value: "127",
        label: "Case Studies",
        note: "Built from real use cases"
    },
    {
        value: "211",
        label: "Orders Received",
        note: "Through digital services"
    }
]

const operationSections = [
    {
        kicker: "DSA growth system",
        title: "Lead management built for everyday DSA execution",
        description: "Capture enquiries, schedule follow-ups, monitor invoices, and understand payout movement from one organized workspace.",
        image: dsaLeanManagement,
        scrollBg: "#88d7ab",
        points: [
            "Smart lead tracking with complete customer history",
            "Sales, invoice, and payout visibility in one place",
            "Performance insights for better daily decisions",
            "Cleaner follow-up discipline without manual diaries"
        ]
    },
    {
        kicker: "Loan Exchange HUB",
        title: "A connected loan exchange network for faster closures",
        description: "Coordinate with the right operational teams, move eligible cases forward, and keep revenue opportunities active across locations.",
        image: dsaLoanExchange,
        scrollBg: "#f2b58e",
        points: [
            "Nationwide DSA collaboration and lead movement",
            "Structured support for complex loan cases",
            "Higher conversion potential through expert routing",
            "A scalable model for branch, area, and regional growth"
        ]
    },
    {
        kicker: "Digital earning tools",
        title: "Online services that create new customer value",
        description: "Offer high-utility digital services directly to retail customers and create more earning opportunities from the same relationship.",
        image: digitalEarningTools,
        scrollBg: "#76c8e8",
        points: [
            "CIBIL, soft sanction, eSign, and eStamp services",
            "Simple online tools customers can understand quickly",
            "Additional income streams beyond one loan product",
            "Transparent workflows that keep every step trackable"
        ]
    }
]


const Home = () => {
        useEffect(() => {
        const counters = document.querySelectorAll(".counter-value");
        const revealItems = document.querySelectorAll(".reveal-on-scroll");
        const panels = [...document.querySelectorAll("[data-scroll-bg]")];
        const intervals = new Set();
const counterIntervals = new Map();
const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const stopCounter = (el) => {
    const activeInterval = counterIntervals.get(el);

    if (activeInterval) {
        clearInterval(activeInterval);
        intervals.delete(activeInterval);
        counterIntervals.delete(el);
    }

    el.textContent = "0";
};

const startCounter = (entry) => {
    const el = entry.target;

    if (counterIntervals.has(el)) return;

    const target = +el.getAttribute("data-count");
    let current = 0;
    const duration = 2000;
    const increment = target / (duration / 16);

    el.textContent = "0";

    const counterInterval = setInterval(() => {
        current += increment;

        if (current >= target) {
            el.textContent = target;
            clearInterval(counterInterval);
            intervals.delete(counterInterval);
            counterIntervals.delete(el);
        } else {
            el.textContent = Math.floor(current);
        }
    }, 16);

    intervals.add(counterInterval);
    counterIntervals.set(el, counterInterval);
};

const counterObserver = new IntersectionObserver(
    (entries) => {
        entries.forEach((entry) => {
            if (entry.isIntersecting) {
                startCounter(entry);
            } else {
                stopCounter(entry.target);
            }
        });
    },
    { threshold: 0.3 }
);


        const revealObserver = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add("is-visible");
                        revealObserver.unobserve(entry.target);
                    }
                });
            },
            { threshold: 0.16, rootMargin: "0px 0px -10% 0px" }
        );

                     const clamp = (value, min = 0, max = 1) => Math.min(Math.max(value, min), max);

        const hexToRgb = (hex) => {
            const value = hex.replace("#", "");
            return {
                r: parseInt(value.slice(0, 2), 16),
                g: parseInt(value.slice(2, 4), 16),
                b: parseInt(value.slice(4, 6), 16),
            };
        };

        const mixColors = (from, to, amount) => {
            const start = hexToRgb(from);
            const end = hexToRgb(to);

            return `rgb(${
                Math.round(start.r + (end.r - start.r) * amount)
            }, ${
                Math.round(start.g + (end.g - start.g) * amount)
            }, ${
                Math.round(start.b + (end.b - start.b) * amount)
            })`;
        };

              const updateScrollEffects = () => {
            const triggerLine = 130;
            const blendDistance = Math.min(280, window.innerHeight * 0.3);
            const transitionStartLine = triggerLine + blendDistance;
            const baseColor = "#fbfdff";

            const statsPanel = document.querySelector(".stats-band");


            const colorPanels = statsPanel
                ? panels.filter((panel) => panel.offsetTop < statsPanel.offsetTop)
                : panels;

            let activeIndex = -1;

            colorPanels.forEach((panel, index) => {
                const titleTarget = panel.querySelector("h2, .text-3xl") || panel;

                if (titleTarget.getBoundingClientRect().top <= transitionStartLine) {
                    activeIndex = index;
                }
            });

            let nextColor = baseColor;

            if (activeIndex >= 0) {
                const activePanel = colorPanels[activeIndex];
                const titleTarget = activePanel.querySelector("h2, .text-3xl") || activePanel;
                const previousColor = activeIndex > 0 ? colorPanels[activeIndex - 1].dataset.scrollBg : baseColor;
                const activeColor = activePanel.dataset.scrollBg;
                const titleTop = titleTarget.getBoundingClientRect().top;
                const blendAmount = clamp((transitionStartLine - titleTop) / blendDistance);

                nextColor = mixColors(previousColor, activeColor, blendAmount);
            }

                 if (statsPanel) {
    const statsTitle = statsPanel.querySelector(".stats-band__heading h2");
    const titleTop = statsTitle?.getBoundingClientRect().top ?? statsPanel.getBoundingClientRect().top;
    const fadeStartLine = window.innerHeight * 0.96;
    const fadeEndLine = window.innerHeight * 0.58;

    if (titleTop <= fadeStartLine) {
        const rawFade = clamp((fadeStartLine - titleTop) / (fadeStartLine - fadeEndLine));
        const smoothFade = rawFade * rawFade * (3 - 2 * rawFade);

        nextColor = mixColors(nextColor, baseColor, smoothFade);
    }

    if (titleTop <= fadeEndLine) {
        nextColor = baseColor;
    }
}
            document.body.style.backgroundColor = nextColor;
            document.documentElement.style.setProperty("--scroll-bg", nextColor);

            const scrollable = document.documentElement.scrollHeight - window.innerHeight;
            const progress = scrollable > 0 ? window.scrollY / scrollable : 0;
            document.documentElement.style.setProperty("--page-progress", progress.toFixed(4));
        };







        let ticking = false;

        const onScroll = () => {
            if (!ticking) {
                requestAnimationFrame(() => {
                    updateScrollEffects();
                    ticking = false;
                });
                ticking = true;
            }
        };

        counters.forEach((counter) => {
    counter.textContent = "0";
    counterObserver.observe(counter);
});


        revealItems.forEach((item) => {
            if (prefersReducedMotion) {
                item.classList.add("is-visible");
            } else {
                revealObserver.observe(item);
            }
        });

        updateScrollEffects();

        window.addEventListener("scroll", onScroll, { passive: true });
        window.addEventListener("resize", updateScrollEffects);

        return () => {
            counterObserver.disconnect();
            revealObserver.disconnect();
            intervals.forEach(clearInterval);
            window.removeEventListener("scroll", onScroll);
            window.removeEventListener("resize", updateScrollEffects);
                        document.body.style.backgroundColor = "";
            document.documentElement.style.removeProperty("--scroll-bg");
            document.documentElement.style.removeProperty("--page-progress");

        };
    }, []);


    return (
        <>
    <div className="scroll-progress" aria-hidden="true" />

   <header id="header" className="header hero-banner">
    <div className="container hero-banner__inner px-4 sm:px-8">
    <div className="hero-copy reveal-on-scroll is-visible">
    <h1 className="hero-title">
    <span>It's not just software,</span>
    <span>It&apos;s a complete </span>
    <span>Loan processing ecosystem.</span>
</h1>

<p className="hero-subtitle">
    Empowering DSAs with Smart Lead Management & Growth Tools. India's first digital ecosystem connecting DSAs, bankers, and customers seamlessly.
</p>


         <div className="hero-actions">
    <Link className="hero-btn hero-btn--primary" to={getServerUrl(dsaRegistrationUrl)} target="_blank" rel="noopener noreferrer">
        <span>Register Now - DSA</span>
    </Link>
    <Link className="hero-btn hero-btn--primary" to={getServerUrl(bankerRegistrationUrl)} target="_blank" rel="noopener noreferrer">
        <span>Register Now - Banker</span>
    </Link>
    <Link className="hero-btn hero-btn--primary" to="/contact">
        <span>Apply Now - Loans</span>
    </Link>
</div>


        </div>

        <div className="hero-device reveal-on-scroll is-visible">
            <div className="hero-device__stage">
                <img src={smartPhone} alt={import.meta.env.VITE_APP_NAME} />
            </div>
        </div>
    </div>
</header>


            <div className="pt-4 pb-14 text-center">
                <div className="container px-4 sm:px-8 xl:px-4">
                    <p className="mb-4 text-gray-800 text-3xl leading-10 lg:max-w-5xl lg:mx-auto"></p>
                </div>
            </div>

          <section
    id="features"
    className="cards-1 home-services mb-16 scroll-panel section-frame"
    data-scroll-bg="#dfe9ff"

>

    <div className="container px-4 sm:px-8 xl:px-4 reveal-on-scroll">
        <div className="home-services__heading">
            <p className="home-services__eyebrow">Our Services</p>
            <h2>Digital services built for faster financial decisions</h2>
            <p>
                Credit insights, pre-approval support, eSign, and eStamp services designed to help DSAs serve customers faster with more clarity.
            </p>
        </div>
    </div>

    <div className="container px-4 sm:px-8 xl:px-4 home-services__grid">
        {services.map((service, index) => (
            <div
                key={index}
                className="service-card-reveal reveal-on-scroll reveal-card"
                style={{ "--reveal-delay": `${index * 90}ms` }}
            >
               <CustomCard
    title={service.title}
    description={service.description}
    image={service.image}
/>

            </div>
        ))}
    </div>
</section>



            <section className="home-trust-intro scroll-panel" data-scroll-bg="#d3b59f">
                <div className="container px-4 sm:px-8 xl:px-4 reveal-on-scroll">
                    <p className="home-section-eyebrow">Why Choose Us</p>
                    <h2 className="home-section-title">A lending platform shaped around automation and speed</h2>
                    <p className="home-section-copy">
                        We brings the daily loan workflow into one clean digital system, helping DSAs serve customers faster while keeping pricing, status, documents, and decisions easier to understand.
                    </p>
                </div>
            </section>


            {details.map((detail, index) => {
                const isReverse = index % 2 !== 0;
                const proof = trustProofs[index];

                const copyBlock = (
                    <div className={`lg:col-span-5 ${isReverse ? "lg:order-2" : ""}`}>
                        <div className={`reveal-on-scroll ${isReverse ? "reveal-right" : "reveal-left"} feature-copy-card`}>
                            <span className="feature-kicker">Platform advantage {String(index + 1).padStart(2, "0")}</span>
                            <h2>{detail.title}</h2>
                            <p>{detail.description}</p>
                            <div className="feature-proof-pill">
                                <strong>{proof.label}</strong>
                                <span>{proof.text}</span>
                            </div>
                        </div>
                    </div>
                );

                const imageBlock = (
                    <div className={`lg:col-span-7 ${isReverse ? "lg:order-1" : ""}`}>
                        <div className={`reveal-on-scroll ${isReverse ? "reveal-left" : "reveal-right"} image-lift feature-visual-card`}>
                            <img src={detail.image} alt={detail.title} />
                        </div>
                    </div>
                );

                return (
                    <section
                        className="container scroll-panel feature-row home-feature-row px-4 sm:px-8 lg:grid lg:grid-cols-12 lg:gap-x-12"
                        data-scroll-bg={isReverse ? "#e9969f" : "#d3b59f"}
                        key={detail.title}
                    >
                        {copyBlock}
                        {imageBlock}
                    </section>
                );
            })}

            <section className="counter stats-band scroll-panel" data-scroll-bg="#ffffff">
                <div className="container px-4 sm:px-8">
                    <div className="stats-band__heading reveal-on-scroll">
                        <p className="home-section-eyebrow">Platform Momentum</p>
                        <h2>Built with measurable confidence for modern loan teams</h2>
                        <p>
                            Every number reflects one simple goal: help DSAs, bankers, and customers move through the loan journey with fewer delays and more clarity.
                        </p>
                    </div>

                    <div id="counter" className="stats-grid">
                        {platformStats.map((stat, index) => (
                            <article
                                className="cell reveal-on-scroll reveal-scale"
                                style={{ "--reveal-delay": `${index * 80}ms` }}
                                key={stat.label}
                            >
                                <div className="counter-value number-count" data-count={stat.value}>0</div>
                                <p className="counter-info">{stat.label}</p>
                                <span className="counter-note">{stat.note}</span>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            {operationSections.map((section, index) => {
                const isReverse = index % 2 !== 0;

                return (
                    <section
                        className="container scroll-panel feature-row home-feature-row home-operation-row px-4 sm:px-8 lg:grid lg:grid-cols-12 lg:gap-x-10"

                        data-scroll-bg={section.scrollBg}
                        key={section.title}
                    >
                        <div className={`lg:col-span-6 ${isReverse ? "lg:order-2" : ""}`}>

                            <div className={`reveal-on-scroll ${isReverse ? "reveal-right" : "reveal-left"} image-lift feature-visual-card feature-visual-card--soft`}>
                                <img src={section.image} alt={section.title} />
                            </div>
                        </div>

                        <div className={`lg:col-span-6 ${isReverse ? "lg:order-1" : ""}`}>

                            <div className={`reveal-on-scroll ${isReverse ? "reveal-left" : "reveal-right"} feature-copy-card feature-copy-card--accent`}>
                                <span className="feature-kicker">{section.kicker}</span>
                                <h2>{section.title}</h2>
                                <p>{section.description}</p>
                                <ul className="home-feature-list">
                                    {section.points.map((point) => (
                                        <li key={point}>
                                            <ChevronRight size={18} />
                                            <span>{point}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </section>
                );
            })}

            <section id="download" className="basic-5 home-download-section md:pb-28! pb-8! scroll-panel" data-scroll-bg="#6fcbd2">
                <div className="download-content home-download-panel container px-4 sm:px-8 lg:grid">
    <div className="download-media reveal-on-scroll reveal-left">

                        <div className="download-video-frame">
                            <video
                                loop
                                muted
                                autoPlay
                                width="320"
                                height="240"
                            >
                                <source src={DashboardVideo} type="video/mp4" />
                            </video>
                        </div>
                    </div>

                    <div className="download-copy reveal-on-scroll reveal-right">

                        <p className="home-section-eyebrow">Mobile App Ecosystem</p>
                        <h2 className="download-title">Run your lending operations from one connected app ecosystem</h2>
                        <p className="download-subtitle">
                            Give DSAs, bankers, and customers the right app experience for their role, with digital services, tracking, and coordination available from anywhere.
                        </p>

                        <div className="download-highlights">
                            <span>DSA workflow</span>
                            <span>Banker decisions</span>
                            <span>Customer tracking</span>
                        </div>

                        <div className="app-download-groups">
                            <div className="app-download-group">
                                <p className="app-download-label">Google Play</p>
                                <div className="app-download-grid">
                                    <Link to={'https://play.google.com/store/apps/details?id=com.ns.ksdpl'} className="btn-solid-lg !flex !items-center !gap-2 !px-4 !py-2 text-white hover:text-black" target="_blank">
                                        <img src={playStoreLogo} className="size-8" alt="Google Play" />
                                        <span className="text-sm">Sales App</span>
                                    </Link>
                                    <Link to={'https://play.google.com/store/apps/details?id=com.dp.ksdpl_bankerapp'} className="btn-solid-lg !flex !items-center !gap-2 !px-4 !py-2 text-white hover:text-black" target="_blank">
                                        <img src={playStoreLogo} className="size-8" alt="Google Play" />
                                        <span className="text-sm">Banker App</span>
                                    </Link>
                                    <Link to={'https://play.google.com/store/apps/details?id=com.dp.ksdpl_customer'} className="btn-solid-lg !flex !items-center !gap-2 !px-4 !py-2 text-white hover:text-black" target="_blank">
                                        <img src={playStoreLogo} className="size-8" alt="Google Play" />
                                        <span className="text-sm">Customer App</span>
                                    </Link>
                                </div>
                            </div>

                            <div className="app-download-group">
                                <p className="app-download-label">Apple - App Store</p>
                                <div className="app-download-grid">
                                    <Link to={'https://apps.apple.com/in/app/ksdpl/id6758232819'} className="btn-solid-lg secondary !flex !items-center !gap-2 !px-4 !py-2 text-white hover:text-black" target="_blank">
                                        <img src={appleStoreLogo} className="size-8" alt="Apple Store" />
                                        <span className="text-sm">Sales App</span>
                                    </Link>
                                    <Link to={'https://apps.apple.com/us/app/myrinbazaar-banker/id6761003904'} className="btn-solid-lg secondary !flex !items-center !gap-2 !px-4 !py-2 text-white hover:text-black" target="_blank">

                                        <img src={appleStoreLogo} className="size-8" alt="Apple Store" />
                                        <span className="text-sm">Banker App</span>
                                    </Link>
                                    <Link to={'https://apps.apple.com/us/app/ksdpl-customer/id6759285045'} className="btn-solid-lg secondary !flex !items-center !gap-2 !px-4 !py-2 text-white hover:text-black" target="_blank">
                                        <img src={appleStoreLogo} className="size-8" alt="Apple Store" />
                                        <span className="text-sm">Customer App</span>
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </>
    )
}

export default Home
